import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { fetchAspQuickResultSummary } from "../../../../redux/action.js";
import i18n from "../../../../utils/i18n";
import { getAspManualFooterProviderCountSnapshot } from "../../utils/aspManualFooterProviderCount.js";
import { ASP_QUICK_RESULT_PACKAGE_SCOPE } from "../quickResult/quickResultProviders";
import classes from "./searchFlowFooterProviderCount.module.scss";

const REFETCH_DEBOUNCE_MS = 300;

const SearchFlowFooterProviderCount = ({
  enabled,
  companyStore,
  matchScoreWeights,
  matchingCount,
  fetchSummary,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const skipNextRefetchRef = useRef(true);

  const filterSnapshot = useMemo(
    () => getAspManualFooterProviderCountSnapshot(companyStore),
    [
      companyStore?.companytype,
      companyStore?.taxform,
      companyStore?.noofemployees,
      companyStore?.vatFilter,
      companyStore?.vatPayment,
      companyStore?.yearlyrevenue,
      companyStore?.foreign_trade,
      companyStore?.foreigntradeEU,
    ],
  );

  useEffect(() => {
    if (!enabled) {
      skipNextRefetchRef.current = true;
      return undefined;
    }

    if (skipNextRefetchRef.current) {
      skipNextRefetchRef.current = false;
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsRefreshing(true);
      fetchSummary(companyStore, matchScoreWeights, {
        aspQuickResultPackageScope:
          ASP_QUICK_RESULT_PACKAGE_SCOPE.ENTREPRENEUR_AND_SMALL_OFFICE,
      }).finally(() => {
        setIsRefreshing(false);
      });
    }, REFETCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // Refetch when filter answers change; companyStore is read at fire time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterSnapshot, fetchSummary, matchScoreWeights]);

  if (!enabled) {
    return null;
  }

  const showSpinner = isRefreshing || matchingCount == null;

  return (
    <div className={classes.root} aria-live="polite">
      {showSpinner ? (
        <span className={classes.loading} aria-label={i18n.t("searchflow.updatingcountaria")}>
          <CircularProgress size={16} sx={{ color: "#1c32f3" }} />
        </span>
      ) : (
        <>
          <p className={classes.line}>
            <span className={classes.count}>{matchingCount}</span>
            {" "}
            {i18n.t("searchflow.accountantnoun")}
          </p>
          <p className={classes.line}>{i18n.t("searchflow.matchesyouranswers")}</p>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  companyStore: state.company_store,
  matchScoreWeights: state.match_score_weights,
  matchingCount: state.company_store?.potProvMatchingCount,
});

const mapDispatchToProps = (dispatch) => ({
  fetchSummary: (companyData, matchScoreWeights, options) =>
    dispatch(fetchAspQuickResultSummary(companyData, matchScoreWeights, options)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SearchFlowFooterProviderCount);
